import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import GameGrid from './components/GameGrid';
import GameDropdown from './components/GameDropdown';
import AdditionalBuylistGrid from './components/AdditionalBuylistGrid';
import AlternativeCart from './components/AlternativeCart';
import CardList from '../components/CardList';
import VideoGamesList from '../components/VideoGamesList';
import SearchBar from './components/SearchBar';
import ExpansionSelector from './components/ExpansionSelector';
import RaritySelector from './components/RaritySelector';
import Pagination from './components/Pagination';
import SaveBuylistPopup from '../components/SaveBuylistPopup';
import LoadBuylistPopup from '../components/LoadBuylistPopup';
import BulkSearchPage from '../components/BulkSearch/BulkSearchPage';
import GradedCardsPopup from '../components/GradedCardsPopup';
import { useNotification } from '../context/NotificationContext';
import './AlternativeApp.css';

const AlternativeApp = ({
  enabledGames,
  cartItems,
  setCartItems,
  importCartItems,
  removeFromCart,
  checkout,
  togglePaymentType,
  clearCart,
  userSettings,
  cards,
  addToCart,
  allowedConditions,
  floorPrice,
  bulkSettings,
  excludedSku,
  calculatePrice,
  exchangeRates,
  handleGameChange,
  handleSetChange,
  searchParams,
  setSearchParams,
  isLoading,
  isInitialLoad,
  setIsInitialLoad,
  customItems = [],
  fetchCustomItems = () => {},
  hotlistItems = [],
  hotlistCashPercent = 70,
  hotlistCreditPercent = 80,
  hotlistFixedPrices = {},
  fetchHotlistItems = () => {},
  additionalBuylistCategories = [],
  additionalBuylistItems = [],
  fetchAdditionalBuylistItems = () => {},
  selectedAdditionalCategory = null,
  username = '',
  showGradedCardsButton = false,
  setShowGradedCardsPopup = () => {},
  paymentSplitData,
  setPaymentSplitData,
  gameSettingsV2 = null
}) => {
  // Check if this is SmokeandMirrors user or v3 template enabled - sidebar should always be open
  const isSmokeandMirrors = username?.toLowerCase() === 'smokeandmirrors' || userSettings?.use_v3_template === true;

  // Currency symbols for formatting prices
  const currencySymbols = {
    USD: '$', EUR: 'â‚¬', GBP: 'Â£', JPY: 'Â¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: 'Â¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: 'â‚©', TRY: 'â‚º', RUB: 'â‚½', INR: 'â‚¹', BRL: 'R$', ZAR: 'R', AED: 'Ø¯.Ø¥'
  };
  const currencySymbol = currencySymbols[userSettings?.currency] || '$';

  const [showCardList, setShowCardList] = useState(false);
  const [showCustomItemsList, setShowCustomItemsList] = useState(false);
  const [showAdditionalBuylistItems, setShowAdditionalBuylistItems] = useState(false);
  const [showBulkSearch, setShowBulkSearch] = useState(false);
  const [showVideoGames, setShowVideoGames] = useState(false);
  const [videoGameConsoleFilter, setVideoGameConsoleFilter] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCartCollapsed, setIsCartCollapsed] = useState(false);
  const [showGradedCardsPopupLocal, setShowGradedCardsPopupLocal] = useState(false);
  // These state values are used in CardList component
  const [showBulkItems, setShowBulkItems] = useState(() => {
    const saved = localStorage.getItem('showBulkItems');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [bulkQuotes, setBulkQuotes] = useState({});
  useEffect(() => {
    let active = true;
    setBulkQuotes({});
    if (showBulkItems || !showCardList || isLoading) return () => { active = false; };
    Promise.all(cards.map(async card => {
      const skus = card.skus || [];
      const condition = skus.find(sku => allowedConditions.includes(sku.condAbbr))?.condAbbr || '';
      const printing = skus[0]?.printingName || '';
      const language = skus[0]?.langAbbr || 'EN';
      const offset = cartItems.filter(item => item.productId === card.productId &&
        item.printing === printing && item.language === language).length + 1;
      try {
        return [card._id, await calculatePrice(card, condition, printing, language, true, false, offset)];
      } catch (error) {
        return [card._id, null];
      }
    })).then(entries => { if (active) setBulkQuotes(Object.fromEntries(entries)); });
    return () => { active = false; };
  }, [cards, allowedConditions, calculatePrice, cartItems, showBulkItems, showCardList, isLoading]);

  // Listen for bulk items toggle events from CardList component
  useEffect(() => {
    const handleBulkItemsToggle = (event) => {
      setShowBulkItems(event.detail.showBulkItems);
    };
    
    window.addEventListener('bulkItemsToggle', handleBulkItemsToggle);
    
    return () => {
      window.removeEventListener('bulkItemsToggle', handleBulkItemsToggle);
    };
  }, []);

  // Fetch hotlist items when component mounts or username changes
  useEffect(() => {
    if (username && isInitialLoad) {
      fetchHotlistItems(username);
    }
  }, [username, isInitialLoad, fetchHotlistItems]);
  const [isTableView] = useState(() => {
    const saved = localStorage.getItem('isTableView');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showSaveBuylistPopup, setShowSaveBuylistPopup] = useState(false);
  const [showLoadBuylistPopup, setShowLoadBuylistPopup] = useState(false);

  // Detect tablet viewport for responsive behavior
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsTablet(width <= 1024 && width > 480);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Helper to detect if a game name is a sealed variant and extract base game
  const parseGameSelection = (gameName) => {
    if (!gameName) return { baseGame: '', isSealed: false };

    const sealedSuffix = ' Sealed';
    if (gameName.toLowerCase().endsWith(sealedSuffix.toLowerCase())) {
      const baseGame = gameName.slice(0, -sealedSuffix.length);
      return { baseGame, isSealed: true };
    }

    return { baseGame: gameName, isSealed: false };
  };

  const handleGameSelect = (game) => {
    // Check if Video Games was selected
    const isVideoGames = game.toLowerCase() === 'video games' || game.toLowerCase() === 'videogames';

    if (isVideoGames) {
      setShowVideoGames(true);
      setShowCardList(false);
      setShowCustomItemsList(false);
      setShowAdditionalBuylistItems(false);
      setIsInitialLoad(false);

      // Auto-hide cart and sidebar when video games is selected
      setIsCartCollapsed(true);
      setIsSidebarCollapsed(true);
      return;
    }

    setShowVideoGames(false);
    setShowCardList(true);
    setShowCustomItemsList(false);
    setShowAdditionalBuylistItems(false);
    setIsInitialLoad(false);

    // Auto-hide cart and sidebar when a game is selected
    setIsCartCollapsed(true);
    setIsSidebarCollapsed(true);

    // Parse the game selection to detect sealed variants
    const { baseGame, isSealed } = parseGameSelection(game);

    setSearchParams(prev => ({
      ...prev,
      game: baseGame,        // Use base game name for API call
      displayGame: game,     // Keep original selection for UI display
      set: '',
      page: 1,
      isSealed: isSealed     // Set sealed flag based on selection
    }));
  };

  const handleAdditionalCategorySelect = (category) => {
    console.log('Selected category:', category);
    setShowAdditionalBuylistItems(true);
    setShowCardList(false);
    setShowCustomItemsList(false);
    setIsInitialLoad(false);

    // Auto-hide cart and sidebar when a category is selected
    setIsCartCollapsed(true);
    setIsSidebarCollapsed(true);

    // Fetch items for this category
    fetchAdditionalBuylistItems(username, category.product_type);
  };

  // This function is kept for future use
  // eslint-disable-next-line no-unused-vars
  const handleBackToGrid = () => {
    setShowCardList(false);
    setShowAdditionalBuylistItems(false);
  };

  const handleOpenSaveBuylistPopup = () => {
    setShowSaveBuylistPopup(true);
  };

  const handleCloseSaveBuylistPopup = () => {
    setShowSaveBuylistPopup(false);
  };

  const handleOpenLoadBuylistPopup = () => {
    setShowLoadBuylistPopup(true);
  };

  const handleCloseLoadBuylistPopup = () => {
    setShowLoadBuylistPopup(false);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleCart = () => {
    setIsCartCollapsed(!isCartCollapsed);
  };
  
  const handleRarityChange = (e) => {
    // Close the custom buylist section when changing rarity
    const altCart = document.querySelector('.alt-cart');
    if (altCart) {
      const customItemsEvent = new CustomEvent('closeCustomItems');
      altCart.dispatchEvent(customItemsEvent);
    }
    
    setSearchParams(prev => ({
      ...prev,
      rarity: e.target.value,
      page: 1
    }));
  };
  
  // Override the handleSetChange prop to close custom items when changing set
  const handleSetChangeWithClose = (e) => {
    // Close the custom buylist section when changing set
    const altCart = document.querySelector('.alt-cart');
    if (altCart) {
      const customItemsEvent = new CustomEvent('closeCustomItems');
      altCart.dispatchEvent(customItemsEvent);
    }
    
    // Call the original handleSetChange from props
    handleSetChange(e);
  };

  const { showSuccess } = useNotification();

  const handleLoadBuylist = async (savedBuylist) => {
    console.log('Loading saved buylist:', savedBuylist);

    // Clear the current cart first
    removeFromCart({ cartItemId: 'all' });

    // Close the popup early so user sees loading progress
    setShowLoadBuylistPopup(false);

    // Add each item to cart with recalculated prices (addToCart handles price recalculation)
    let addedCount = 0;
    for (const item of savedBuylist.cart_items) {
      try {
        // Add to cart using the addToCart function from props
        // addToCart recalculates prices with inventory-aware logic
        await addToCart(
          item,
          item.condition,
          item.printing,
          item.quantity || 1,
          item.skuId,
          item.isCash,
          item.language || 'EN',
          item.orderNote || '',
          item.nonEnglish || null
        );
        addedCount++;
      } catch (err) {
        console.error(`Error adding item ${item.name} to cart:`, err);
      }
    }

    // Show a success message with item count
    showSuccess(`Buylist loaded successfully! Added ${addedCount} item${addedCount !== 1 ? 's' : ''}.`);
  };

  return (
    <div className="alt-app">
      {showSaveBuylistPopup && (
        <SaveBuylistPopup
          onClose={handleCloseSaveBuylistPopup}
          cartItems={cartItems}
          username={userSettings?.username || 'guest'}
        />
      )}
      {showLoadBuylistPopup && (
        <LoadBuylistPopup
          onClose={handleCloseLoadBuylistPopup}
          onLoadBuylist={handleLoadBuylist}
          username={userSettings?.username || 'guest'}
        />
      )}
      {showGradedCardsPopupLocal && (
        <GradedCardsPopup
          onClose={() => setShowGradedCardsPopupLocal(false)}
          username={username}
          userSettings={userSettings}
        />
      )}
      {/* Price change notification removed as we're keeping original prices */}
      <div className="alt-main-container">
        {/* Sidebar collapse button - only show when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button
            className="alt-sidebar-expand-button"
            onClick={toggleSidebar}
            title="Show Games Sidebar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18m-9-9l9 9-9 9"/>
            </svg>
            <span>Games</span>
          </button>
        )}

        <div className={`alt-sidebar-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          {!isSidebarCollapsed && (
            <>
              <button
                className="alt-sidebar-collapse-button"
                onClick={toggleSidebar}
                title="Hide Sidebar"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 12H3m18-9l-9 9 9 9"/>
                </svg>
                <span>Hide</span>
              </button>
              <Sidebar
                enabledGames={enabledGames}
                onGameSelect={handleGameSelect}
                customItems={customItems}
                userSettings={userSettings}
                hotlistItems={hotlistItems}
                onCustomItemsClick={() => {
                  // Show custom items in the main content area
                  setShowCardList(false);
                  setShowCustomItemsList(true);
                }}
                onHotlistClick={() => {
                  // Show hotlist in the main content area
                  setShowCardList(true);
                  setShowCustomItemsList(false);
                  setShowBulkSearch(false);
                  setIsInitialLoad(true);
                  setSearchParams({
                    term: '',
                    abbr: '',
                    number: '',
                    game: '',
                    set: '',
                    rarity: '',
                    page: 1,
                    sort: 'name_asc'
                  });
                  // Fetch hotlist items
                  fetchHotlistItems(username);
                }}
              />
            </>
          )}
        </div>
        
        <div className="alt-content-container">
          {/* Game Grid at the top when not in any specific view */}
          {!showBulkSearch && !showCustomItemsList && !showCardList && !showAdditionalBuylistItems && !showVideoGames && (
            <>
              <GameGrid
                enabledGames={enabledGames}
                onGameSelect={handleGameSelect}
                userSettings={userSettings}
                gameSettingsV2={gameSettingsV2}
              />
              <AdditionalBuylistGrid
                categories={additionalBuylistCategories}
                onCategorySelect={handleAdditionalCategorySelect}
              />
              {showGradedCardsButton && (
                <div className="alt-game-grid" style={{ marginTop: '2rem' }}>
                  <h2>Graded Cards</h2>
                  <div className="alt-grid-container">
                    <div
                      className="alt-game-card"
                      onClick={() => setShowGradedCardsPopupLocal(true)}
                    >
                      <div className="alt-game-image-container">
                        <div className="alt-game-image">
                          <div
                            className="alt-game-image-inner"
                            style={{
                              backgroundColor: '#ff9800',
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '8px'
                            }}
                          >
                            <span style={{ color: 'white', fontWeight: 'bold', fontSize: '20px' }}>
                              G
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="alt-game-title">
                        Graded Cards
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {showBulkSearch ? (
            <div className="alt-bulk-search-view">
              <BulkSearchPage
                addToCart={addToCart}
                userSettings={userSettings}
                allowedConditions={allowedConditions}
                calculatePrice={calculatePrice}
                exchangeRates={exchangeRates}
                hotlistItems={hotlistItems}
                hotlistCashPercent={hotlistCashPercent}
                hotlistCreditPercent={hotlistCreditPercent}
                hotlistFixedPrices={hotlistFixedPrices}
                showSearchModeToggle={true}
                currentSearchMode="bulk"
                onSearchModeToggle={() => setShowBulkSearch(false)}
              />
            </div>
          ) : showCustomItemsList ? (
            <div className="alt-custom-items-view">
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle">
                  <button
                    className="alt-search-mode-button"
                    onClick={() => {
                      setShowCardList(true);
                      setShowCustomItemsList(false);
                    }}
                  >
                    Back to Search
                  </button>
                </div>
              )}
              <h2>{userSettings?.custom_buylist_button_label || 'Other Items We Buy'}</h2>
              <div className="alt-custom-items-list">
                <table className="alt-custom-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Description</th>
                      <th>Cash Price</th>
                      <th>Credit Price</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customItems.map((item) => (
                      <tr key={item._id.$oid}>
                        <td>{item.title}</td>
                        <td>{item.description}</td>
                        <td>{currencySymbol}{item.cash_price}</td>
                        <td>{currencySymbol}{item.credit_price}</td>
                        <td>
                          <input 
                            type="number" 
                            min="1" 
                            max="999" 
                            defaultValue="1"
                            className="alt-custom-item-quantity"
                          />
                        </td>
                        <td>
                          <button 
                            className="alt-add-custom-item-cash"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: true,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              addToCart(
                                customItem,
                                customItem.condition,
                                customItem.printing,
                                customItem.quantity,
                                null,
                                customItem.isCash,
                                'EN',
                                '',
                                null
                              );
                              showSuccess(`Added ${item.title} to cart (Cash)`);
                            }}
                          >
                            Add (Cash)
                          </button>
                          <button 
                            className="alt-add-custom-item-credit"
                            onClick={(e) => {
                              const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity').value);
                              const customItem = {
                                name: item.title,
                                description: item.description,
                                cashPrice: item.cash_price,
                                creditPrice: item.credit_price,
                                quantity: quantity,
                                isCash: false,
                                cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                                condition: 'N/A',
                                printing: 'N/A',
                                isCustomItem: true
                              };
                              addToCart(
                                customItem,
                                customItem.condition,
                                customItem.printing,
                                customItem.quantity,
                                null,
                                customItem.isCash,
                                'EN',
                                '',
                                null
                              );
                              showSuccess(`Added ${item.title} to cart (Credit)`);
                            }}
                          >
                            Add (Credit)
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : showAdditionalBuylistItems ? (
            <div className="alt-custom-items-view">
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle">
                  <button
                    className="alt-search-mode-button"
                    onClick={() => {
                      setShowAdditionalBuylistItems(false);
                    }}
                  >
                    Back to Selection
                  </button>
                </div>
              )}
              <h2>{selectedAdditionalCategory?.button_label || 'Additional Items'}</h2>
              {selectedAdditionalCategory?.description && (
                <div className="additional-buylist-description">
                  <p>{selectedAdditionalCategory.description}</p>
                </div>
              )}
              <div className="alt-custom-items-list">
                <table className="alt-custom-items-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product</th>
                      <th>Cash Price</th>
                      <th>Credit Price</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalBuylistItems.map((product) => (
                      product.variants
                        .filter(variant => {
                          const cashPrice = parseFloat(variant.cashPrice) || 0;
                          const creditPrice = parseFloat(variant.creditPrice) || 0;
                          return cashPrice > 0 || creditPrice > 0;
                        })
                        .map((variant, variantIndex) => (
                        <tr key={`${product.productId}-${variantIndex}`}>
                          <td>
                            {(variant.image || product.image) && (
                              <img
                                src={variant.image || product.image}
                                alt={product.title}
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            )}
                          </td>
                          <td>{product.title}{variant.title !== 'Default Title' && ` - ${variant.title}`}</td>
                          <td>{currencySymbol}{variant.cashPrice}</td>
                          <td>{currencySymbol}{variant.creditPrice}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              max="999"
                              defaultValue="1"
                              className="alt-custom-item-quantity"
                            />
                          </td>
                          <td>
                            <button
                              className="alt-add-custom-item-cash"
                              onClick={(e) => {
                                const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity').value);
                                const basePrice = parseFloat(variant.price);
                                const additionalItem = {
                                  name: `${product.title} - ${variant.title}`,
                                  cashPrice: parseFloat(variant.cashPrice),
                                  creditPrice: parseFloat(variant.creditPrice),
                                  quantity: quantity,
                                  isCash: true,
                                  cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                  condition: 'N/A',
                                  printing: 'N/A',
                                  isAdditionalBuylist: true,
                                  productType: selectedAdditionalCategory?.product_type,
                                  basePrice: basePrice,
                                  originalBasePrice: basePrice
                                };
                                addToCart(
                                  additionalItem,
                                  additionalItem.condition,
                                  additionalItem.printing,
                                  additionalItem.quantity,
                                  null,
                                  additionalItem.isCash,
                                  'EN',
                                  '',
                                  null
                                );
                                showSuccess(`Added ${product.title} to cart (Cash)`);
                              }}
                            >
                              Add (Cash)
                            </button>
                            <button
                              className="alt-add-custom-item-credit"
                              onClick={(e) => {
                                const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.alt-custom-item-quantity').value);
                                const basePrice = parseFloat(variant.price);
                                const additionalItem = {
                                  name: `${product.title} - ${variant.title}`,
                                  cashPrice: parseFloat(variant.cashPrice),
                                  creditPrice: parseFloat(variant.creditPrice),
                                  quantity: quantity,
                                  isCash: false,
                                  cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                                  condition: 'N/A',
                                  printing: 'N/A',
                                  isAdditionalBuylist: true,
                                  productType: selectedAdditionalCategory?.product_type,
                                  basePrice: basePrice,
                                  originalBasePrice: basePrice
                                };
                                addToCart(
                                  additionalItem,
                                  additionalItem.condition,
                                  additionalItem.printing,
                                  additionalItem.quantity,
                                  null,
                                  additionalItem.isCash,
                                  'EN',
                                  '',
                                  null
                                );
                                showSuccess(`Added ${product.title} to cart (Credit)`);
                              }}
                            >
                              Add (Credit)
                            </button>
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : showVideoGames ? (
            <div className="alt-video-games-view">
              {!isSmokeandMirrors && (
                <div className="alt-search-mode-toggle">
                  <button
                    className="alt-search-mode-button"
                    onClick={() => {
                      setShowVideoGames(false);
                    }}
                  >
                    Back to Games
                  </button>
                </div>
              )}
              <VideoGamesList
                userSettings={userSettings}
                addToCart={addToCart}
                exchangeRates={exchangeRates}
                consoleFilter={videoGameConsoleFilter}
                onConsoleChange={setVideoGameConsoleFilter}
              />
            </div>
          ) : showCardList ? (
            <div className="alt-card-list-view">
              <div className="alt-search-container">
                {/* Game dropdown for easy switching on tablets */}
                {isTablet && (
                  <GameDropdown
                    games={enabledGames}
                    selectedGame={searchParams.game}
                    onGameChange={handleGameSelect}
                    gameSettingsV2={gameSettingsV2}
                  />
                )}

                <SearchBar
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setIsInitialLoad={setIsInitialLoad}
                />

                <div className="alt-filters-row">
                  <ExpansionSelector
                    allExpansionNames={searchParams.allExpansionNames || []}
                    searchParams={searchParams}
                    handleSetChange={handleSetChangeWithClose}
                  />

                  <RaritySelector
                    allRarities={searchParams.allRarities || []}
                    searchParams={searchParams}
                    handleRarityChange={handleRarityChange}
                  />
                </div>
              </div>
              
              {isLoading ? (
                <div className="alt-loading">Loading...</div>
              ) : (
                <>
                  {/* Filter bulk items before rendering if showBulkItems is false */}
                  {(() => {
                    // Quotes are resolved outside rendering; only the shared API decides bulk status.
                    const filteredCards = showBulkItems
                      ? cards
                      : cards.filter(card => bulkQuotes[card._id]?.isBulk !== true);

                    // We'll use the server-provided total pages for pagination
                    // This prevents client-side filtering from interfering with server pagination
                    const totalFilteredPages = showBulkItems 
                      ? searchParams.totalPages 
                      : Math.ceil(filteredCards.length / 10); // 10 items per page for client-side filtering
                    
                    return (
                      <>
                        <CardList
                          cards={filteredCards}
                          addToCart={addToCart}
                          allowedConditions={allowedConditions}
                          floorPrice={floorPrice}
                          bulkSettings={bulkSettings}
                          userSettings={userSettings}
                          excludedSku={excludedSku}
                          calculatePrice={calculatePrice}
                          exchangeRates={exchangeRates}
                          showBulkItems={true} // Always true since we've already filtered
                          isTableView={isTableView}
                          isCashLimitReached={userSettings?.cash_limit &&
                            cartItems.reduce((sum, item) => sum + (item.isCash ? parseFloat(item.cashPrice) * item.quantity : 0), 0) >= userSettings.cash_limit}
                          hotlistItems={hotlistItems}
                          hotlistCashPercent={hotlistCashPercent}
                          hotlistCreditPercent={hotlistCreditPercent}
                          hotlistFixedPrices={hotlistFixedPrices}
                          showSearchModeToggle={true}
                          currentSearchMode="single"
                          onSearchModeToggle={() => setShowBulkSearch(true)}
                          cartItems={cartItems}
                        />
                        
                        {/* Pagination or "Showing all results" message */}
                        <div className="alt-pagination-container">
                          {(showBulkItems ? searchParams.totalPages : totalFilteredPages) > 1 ? (
                            <Pagination
                              currentPage={parseInt(searchParams.page) || 1}
                              totalPages={showBulkItems ? searchParams.totalPages : totalFilteredPages}
                              onPageChange={(page) => {
                                // Close the custom buylist section when changing page
                                const altCart = document.querySelector('.alt-cart');
                                if (altCart) {
                                  const customItemsEvent = new CustomEvent('closeCustomItems');
                                  altCart.dispatchEvent(customItemsEvent);
                                }
                                
                                setSearchParams(prev => ({
                                  ...prev,
                                  page: page
                                }));
                              }}
                            />
                          ) : (
                            filteredCards.length > 0 && (
                              <div className="showing-all-results">
                                Showing all {filteredCards.length} results
                              </div>
                            )
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          ) : null}
        </div>
        
        <div className={`alt-cart-container ${isCartCollapsed ? 'collapsed' : ''}`}>
          {!isCartCollapsed && (
            <>
              <button
                className="alt-cart-collapse-button"
                onClick={toggleCart}
                title="Hide Cart"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18m-9-9l9 9-9 9"/>
                </svg>
                <span>Hide</span>
              </button>
              <AlternativeCart
                cartItems={cartItems}
                removeFromCart={removeFromCart}
                checkout={checkout}
                togglePaymentType={togglePaymentType}
                clearCart={clearCart}
                userSettings={userSettings}
                onSaveForLater={handleOpenSaveBuylistPopup}
                onLoadBuylist={handleOpenLoadBuylistPopup}
                customItems={customItems}
                fetchCustomItems={fetchCustomItems}
                setCartItems={setCartItems}
                importCartItems={importCartItems}
                calculatePrice={calculatePrice}
                allowedConditions={allowedConditions}
                addToCart={addToCart}
                paymentSplitData={paymentSplitData}
                setPaymentSplitData={setPaymentSplitData}
              />
            </>
          )}
        </div>

        {/* Cart expand button - only show when cart is collapsed */}
        {isCartCollapsed && (
          <button
            className="alt-cart-expand-button"
            onClick={toggleCart}
            title="Show Shopping Cart"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12H3m18-9l-9 9 9 9"/>
            </svg>
            <span>Cart ({cartItems.length})</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default AlternativeApp;
